import { useState } from 'react'
import { Icon } from '@/design-system'
import { asset } from '@/lib/assets'
import { cn } from '@/lib/cn'
import styles from './Media.module.css'

export interface MediaProps {
  /** Path inside the asset bucket, e.g. `travel/hotels/casa-polanco.jpg`. */
  readonly src: string
  readonly alt: string
  /** Aspect ratio as width / height. Defaults to 3:2. */
  readonly ratio?: number
  /** Fixed width; omit to fill the container. */
  readonly width?: number | string
  readonly radius?: 'md' | 'lg' | 'circle'
  /** Icon shown while loading and if the file never arrives. */
  readonly icon?: string
  readonly className?: string | undefined
  /** Above the fold: skips lazy loading so it is not deferred. */
  readonly eager?: boolean
}

export function Media({
  src,
  alt,
  ratio = 3 / 2,
  width,
  radius = 'md',
  icon = 'image',
  className,
  eager = false,
}: MediaProps) {
  const [state, setState] = useState<'loading' | 'loaded' | 'failed'>('loading')

  return (
    <div
      className={cn(
        styles.frame,
        radius === 'lg' && styles.rounded,
        radius === 'circle' && styles.circle,
        className,
      )}
      style={{ '--media-ratio': String(ratio), width } as React.CSSProperties}
    >
      {state !== 'loaded' && (
        <div className={styles.fallback} aria-hidden="true">
          <Icon name={icon} size={22} />
        </div>
      )}
      <img
        src={asset(src)}
        alt={alt}
        className={cn(styles.image, state === 'loaded' && styles.loaded)}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={() => setState('loaded')}
        onError={() => setState('failed')}
      />
    </div>
  )
}
